import { Fragment, type ReactNode } from 'react';

import { formatCanonicalServiceLabel } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_labels';
import { ArtifactDownloadButton } from '@apps/web/routes/_app/admin/agent/artifacts/download_button';
import { type ApiResponseType } from '@libraries/typescript/api_client';

const UNTRUSTED_START_MARKER = '<untrusted-data>';
const UNTRUSTED_END_MARKER = '</untrusted-data>';

export type AdminAgentArtifact = ApiResponseType<
    '/admin/agent/runs/{run_id}/artifacts/{artifact_id}',
    'get'
>;

type ArtifactFrameRef = {
    readonly artifact_type?: string | null;
    readonly artifact_version?: number | null;
    readonly summary?: string | null;
    readonly title?: string | null;
};

function ActivityDot({ tone }: { readonly tone: 'working' | 'failed' }) {
    if (tone === 'failed') {
        return <span className="size-1.5 shrink-0 rounded-full bg-red-600" />;
    }
    return (
        <span className="relative flex size-1.5 shrink-0">
            <span className="bg-foreground/40 absolute inline-flex h-full w-full animate-ping rounded-full" />
            <span className="bg-foreground/60 relative inline-flex size-1.5 rounded-full" />
        </span>
    );
}

// Completed is the unexceptional state and renders nothing; only in-flight and
// failed artifacts earn an indicator.
export function ArtifactLifecycleStatusBadge({
    lifecycleStatus,
}: {
    readonly lifecycleStatus?: string | null;
}) {
    const normalizedStatus = lifecycleStatus?.trim().toLowerCase();
    if (normalizedStatus === 'failed') {
        return (
            <span className="flex items-center gap-1.5 text-[11px] font-medium text-red-700">
                <ActivityDot tone="failed" />
                Failed
            </span>
        );
    }
    if (normalizedStatus === 'running' || normalizedStatus === 'announced') {
        return (
            <span className="text-muted-foreground flex items-center gap-1.5 text-[11px]">
                <ActivityDot tone="working" />
                {normalizedStatus === 'running' ? 'Running' : 'Preparing'}
            </span>
        );
    }
    return null;
}

export function ArtifactLoadingStatusBadge() {
    return (
        <span className="text-muted-foreground flex items-center gap-1.5 text-[11px]">
            <ActivityDot tone="working" />
            Loading
        </span>
    );
}

export function ArtifactFrame({
    artifact,
    artifactRef,
    status,
    title,
    children,
}: {
    readonly artifact?: AdminAgentArtifact;
    readonly artifactRef?: ArtifactFrameRef;
    readonly status: ReactNode;
    readonly title?: string;
    readonly children?: ReactNode;
}) {
    const frameTitle =
        title ?? artifact?.title ?? artifactRef?.title ?? 'Admin artifact';
    const summary = artifact?.summary ?? artifactRef?.summary ?? null;
    const artifactType = artifact?.artifact_type ?? artifactRef?.artifact_type;
    const artifactVersion =
        artifact?.artifact_version ?? artifactRef?.artifact_version ?? 1;
    // The surrounding tool card provides containment, so the frame stays flat
    // and artifacts never render as a box inside a box.
    //
    // Header layout notes:
    // - The header wraps (`flex-wrap`) so status chips drop onto a second
    //   line in a narrow sidebar rather than crushing the title into
    //   one-word-per-line width.
    // - The title column carries both `min-w-0` and `break-words` so long
    //   titles wrap gracefully instead of overflowing across sibling nodes.
    // - The artifact type badge is a separate row when a status chip is
    //   present, so the two never overlap regardless of container width.
    return (
        <section className="space-y-2.5">
            <div className="flex min-w-0 flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
                <div className="min-w-0 flex-1 basis-[14rem]">
                    <h4 className="text-foreground text-[13px] leading-snug font-medium tracking-[-0.01em] break-words">
                        {frameTitle}
                    </h4>
                    {summary ? (
                        <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed break-words">
                            {summary}
                        </p>
                    ) : null}
                </div>
                {status || artifactType || artifact ? (
                    <div className="flex max-w-full min-w-0 shrink-0 flex-wrap items-center gap-x-2.5 gap-y-1 pt-0.5">
                        {status}
                        {artifactType ? (
                            <span className="text-muted-foreground/60 max-w-full font-mono text-[10px] break-all">
                                {artifactType}@{artifactVersion}
                            </span>
                        ) : null}
                        {artifact ? (
                            <ArtifactDownloadButton artifact={artifact} />
                        ) : null}
                    </div>
                ) : null}
            </div>
            {children}
        </section>
    );
}

// Provenance/spec metadata stays reachable (information parity) but collapsed
// by default so the artifact content itself leads the card. Tailwind classes
// mirror the scoped .aa-label micro-label because ArtifactFrame also renders
// outside .admin-agent-page (e.g. /admin/agent-traces).
export function ArtifactMetaDisclosure({
    children,
}: {
    readonly children: ReactNode;
}) {
    return (
        <details>
            <summary className="aa-label text-muted-foreground hover:text-foreground cursor-pointer font-mono text-[10px] tracking-[0.05em] uppercase transition-colors">
                details
            </summary>
            <div className="mt-2 space-y-2.5">{children}</div>
        </details>
    );
}

export function ArtifactEvidence({
    artifact,
}: {
    readonly artifact: AdminAgentArtifact;
}) {
    const evidence = toRecord(artifact.evidence);
    const source = toRecord(artifact.source);
    const sourceCoverage = Array.isArray(evidence['source_coverage'])
        ? evidence['source_coverage'].filter(isRecord)
        : [];
    const filters = toRecord(source['filters_applied']);
    const hasFilters = Object.keys(filters).length > 0;

    const metaSegments: Array<{
        readonly key: string;
        readonly node: ReactNode;
    }> = [];
    const toolName = scalarText(source['tool']);
    if (toolName) {
        metaSegments.push({
            key: 'tool',
            node: <span className="font-mono">{toolName}</span>,
        });
    }
    const environmentName = scalarText(source['environment']);
    if (environmentName) {
        metaSegments.push({
            key: 'environment',
            node: <span>{environmentName}</span>,
        });
    }
    const confidenceLabel = scalarText(evidence['confidence']);
    if (confidenceLabel) {
        metaSegments.push({
            key: 'confidence',
            node: <span>{confidenceLabel} confidence</span>,
        });
    }
    if (evidence['raw_data_available'] !== undefined) {
        const truncatedSuffix =
            evidence['raw_data_truncated'] === true ? ', truncated' : '';
        metaSegments.push({
            key: 'raw',
            node: (
                <span>
                    {evidence['raw_data_available'] === true
                        ? 'Available'
                        : 'Not available'}
                    {truncatedSuffix}
                </span>
            ),
        });
    }

    if (
        metaSegments.length === 0 &&
        !hasFilters &&
        artifact.caveats.length === 0 &&
        sourceCoverage.length === 0
    ) {
        return null;
    }

    return (
        <div className="space-y-2.5">
            {metaSegments.length > 0 ? (
                <p className="text-muted-foreground text-[11px] break-words">
                    {metaSegments.map((segment, index) => (
                        <Fragment key={segment.key}>
                            {index > 0 ? (
                                <span className="text-muted-foreground/40">
                                    {' · '}
                                </span>
                            ) : null}
                            {segment.node}
                        </Fragment>
                    ))}
                </p>
            ) : null}
            {hasFilters ? (
                <p className="text-muted-foreground text-[11px] break-words">
                    <span className="text-muted-foreground/60">Filters</span>{' '}
                    {renderUntrustedString(formatRecordInline(filters))}
                </p>
            ) : null}
            {artifact.caveats.length > 0 ? (
                <ul className="space-y-1">
                    {artifact.caveats.map((caveat) => (
                        <li
                            key={caveat}
                            className="border-border text-muted-foreground border-l pl-2.5 text-xs leading-relaxed break-words"
                        >
                            {caveat}
                        </li>
                    ))}
                </ul>
            ) : null}
            {sourceCoverage.length > 0 ? (
                <p className="text-xs break-words">
                    {sourceCoverage.map((sourceItem, index) => (
                        <span key={String(sourceItem['source_name'])}>
                            {index > 0 ? (
                                <span className="text-muted-foreground/40">
                                    {' · '}
                                </span>
                            ) : null}
                            <span
                                className={
                                    sourceItem['checked'] === true
                                        ? 'text-foreground'
                                        : 'text-muted-foreground/60'
                                }
                            >
                                {sourceItem['checked'] === true
                                    ? 'Checked '
                                    : 'Skipped '}
                                {formatSourceName(sourceItem['source_name'])}
                            </span>
                        </span>
                    ))}
                </p>
            ) : null}
        </div>
    );
}

function scalarText(value: unknown): string | null {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    return null;
}

function renderUntrustedString(value: string): ReactNode {
    const nodes: Array<ReactNode> = [];
    let cursor = 0;
    let markerIndex = 0;

    while (cursor < value.length) {
        const start = value.indexOf(UNTRUSTED_START_MARKER, cursor);
        if (start < 0) {
            nodes.push(value.slice(cursor));
            break;
        }

        const untrustedStart = start + UNTRUSTED_START_MARKER.length;
        const end = value.indexOf(UNTRUSTED_END_MARKER, untrustedStart);
        if (end < 0) {
            nodes.push(value.slice(cursor));
            break;
        }

        if (start > cursor) {
            nodes.push(value.slice(cursor, start));
        }

        nodes.push(
            <mark
                key={`untrusted-${markerIndex}`}
                className="rounded-[3px] px-1 py-0.5"
                style={{
                    // Hex fallbacks mirror the --aa-warning* tokens in
                    // theme.css: the tokens are only defined on
                    // .admin-agent-page, but ArtifactFrame is also
                    // rendered from /admin/agent-traces via
                    // createAdminToolArtifactRenderer, where the scope
                    // is absent and an unfallbacked var() would resolve
                    // to the inherited text color.
                    background: 'var(--aa-warning-soft, #fbf3df)',
                    color: 'var(--aa-warning, #a06a0a)',
                }}
            >
                {value.slice(untrustedStart, end)}
            </mark>,
        );
        markerIndex += 1;
        cursor = end + UNTRUSTED_END_MARKER.length;
    }

    if (nodes.length === 0) {
        return value;
    }

    return <Fragment>{nodes}</Fragment>;
}

function toRecord(value: unknown): Record<string, unknown> {
    return isRecord(value) ? value : {};
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function formatSourceName(value: unknown): string {
    if (typeof value === 'string' && value.trim()) {
        return formatCanonicalServiceLabel(value);
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    return 'Unknown source';
}

function formatRecordInline(value: Record<string, unknown>): string {
    const entries = Object.entries(value);
    if (entries.length === 0) {
        return 'None';
    }
    return entries
        .map(([key, rawValue]) => `${key}: ${formatFilterValue(key, rawValue)}`)
        .join(', ');
}

function formatFilterValue(key: string, value: unknown): string {
    if (isSensitiveKey(key)) {
        return '[redacted]';
    }
    if (typeof value === 'string') {
        if (value.includes(UNTRUSTED_START_MARKER)) {
            return value;
        }
        return value.length > 80 ? `${value.slice(0, 80)}...` : value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (value === null || value === undefined) {
        return 'None';
    }
    return '[structured]';
}

function isSensitiveKey(key: string): boolean {
    const normalized = key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
    const tokens = normalized.split(/[^a-z0-9]+/).filter(Boolean);
    const compact = tokens.join('');
    return (
        tokens.some((token) =>
            [
                'auth',
                'authorization',
                'credential',
                'credentials',
                'key',
                'password',
                'secret',
                'token',
            ].includes(token),
        ) || compact.includes('apikey')
    );
}
